<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <component
        v-for="doc in mappedDocuments"
        :key="doc.index"
        :is="getComponentForType(doc.type)"
        :document="doc"
        @preview-requested="openPreviewFromAttachment"
      />
    </div>

    <!-- Modal de prévisualisation -->
    <div
      v-if="previewModal.open"
      class="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      @click.self="closePreview"
    >
      <div class="relative w-full h-full max-w-7xl max-h-[90vh] flex flex-col" role="dialog" aria-modal="true">
        <!-- En-tête du modal -->
        <div class="flex items-center justify-between bg-gray-900 text-white p-4 rounded-t-lg">
          <h3 class="text-lg font-semibold truncate flex-1">{{ previewModal.fileName }}</h3>
          <button
            @click="closePreview"
            class="ml-4 p-2 hover:bg-gray-800 rounded-lg transition-colors"
            title="Fermer"
            aria-label="Fermer la prévisualisation"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Contenu du modal -->
        <div class="flex-1 overflow-auto bg-gray-900 p-4">
          <!-- Image Preview -->
          <div v-if="previewModal.type === 'image'" class="flex items-center justify-center h-full">
            <img
              :src="previewModal.url"
              :alt="previewModal.fileName"
              class="max-w-full max-h-full object-contain"
            />
          </div>

          <!-- PDF Preview -->
          <div v-else-if="previewModal.type === 'pdf'" class="h-full">
            <iframe
              :src="previewModal.url + '#toolbar=1'"
              class="w-full h-full border-0"
              frameborder="0"
            ></iframe>
          </div>
        </div>

        <!-- Boutons de navigation (si plusieurs documents) -->
        <div v-if="mappedDocuments.length > 1" class="flex items-center justify-between bg-gray-900 text-white p-4 rounded-b-lg">
          <button
            @click="previousDocument"
            :disabled="previewModal.index === 0"
            class="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :title="previewModal.index === 0 ? 'Premier document' : 'Document précédent'"
          >
            ← Précédent
          </button>
          <span class="text-sm text-gray-400">
            {{ previewModal.index + 1 }} / {{ mappedDocuments.length }}
          </span>
          <button
            @click="nextDocument"
            :disabled="previewModal.index === mappedDocuments.length - 1"
            class="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :title="previewModal.index === mappedDocuments.length - 1 ? 'Dernier document' : 'Document suivant'"
          >
            Suivant →
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import ImageAttachmentCard from './documents/ImageAttachmentCard.vue'
import PdfAttachmentCard from './documents/PdfAttachmentCard.vue'
import OfficeAttachmentCard from './documents/OfficeAttachmentCard.vue'
import GenericAttachmentCard from './documents/GenericAttachmentCard.vue'

interface Props {
  documents: string[]
  baseUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  baseUrl: ''
})

type AttachmentType = 'image' | 'pdf' | 'word' | 'excel' | 'powerpoint' | 'cv' | 'file'

interface AttachmentDocument {
  raw: string
  url: string
  name: string
  ext: string
  type: AttachmentType
  index: number
}

const previewModal = ref({
  open: false,
  url: '',
  fileName: '',
  type: '' as 'image' | 'pdf' | 'other',
  index: 0
})

// Types d'images supportés
const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
const pdfExtensions = ['pdf']

const getFileName = (doc: string): string => {
  if (!doc) return 'Document'

  // Si la chaîne contient un paramètre de requête filename=, l'utiliser en priorité
  if (doc.includes('filename=')) {
    const match = /filename=([^&]+)/i.exec(doc)
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1])
      } catch {
        return match[1]
      }
    }
  }

  // Si c'est une URL complète, extraire le nom du fichier
  if (doc.includes('/')) {
    const parts = doc.split('/')
    return decodeURIComponent(parts[parts.length - 1])
  }
  
  return doc
}

const getFileExtension = (doc: string): string => {
  const fileName = getFileName(doc)
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

const getDocumentUrl = (doc: string): string => {
  // Si c'est déjà une URL complète, la retourner telle quelle
  if (doc.startsWith('http://') || doc.startsWith('https://')) {
    return doc
  }
  
  // Sinon, construire l'URL avec la base URL si fournie
  if (props.baseUrl) {
    const base = props.baseUrl.endsWith('/') ? props.baseUrl.slice(0, -1) : props.baseUrl
    const docPath = doc.startsWith('/') ? doc : `/${doc}`
    return `${base}${docPath}`
  }
  
  return doc
}

const isImage = (doc: string): boolean => {
  const ext = getFileExtension(doc)
  return imageExtensions.includes(ext)
}

const isPdf = (doc: string): boolean => {
  const ext = getFileExtension(doc)
  return pdfExtensions.includes(ext)
}

const getFileType = (doc: string): AttachmentType => {
  const ext = getFileExtension(doc)
  
  if (imageExtensions.includes(ext)) return 'image'
  if (pdfExtensions.includes(ext)) return 'pdf'
  if (['doc', 'docx'].includes(ext)) return 'word'
  if (['xls', 'xlsx'].includes(ext)) return 'excel'
  if (['ppt', 'pptx'].includes(ext)) return 'powerpoint'
  if (ext === 'cv') return 'cv'
  
  return 'file'
}

const mappedDocuments = computed<AttachmentDocument[]>(() =>
  props.documents.map((doc, index) => {
    const name = getFileName(doc)
    const ext = getFileExtension(doc)
    const type = getFileType(doc)
    const url = getDocumentUrl(doc)

    return {
      raw: doc,
      url,
      name,
      ext,
      type,
      index
    }
  })
)

const openPreviewFromAttachment = (attachment: AttachmentDocument) => {
  const previewType: 'image' | 'pdf' | 'other' =
    attachment.type === 'image' ? 'image' : attachment.type === 'pdf' ? 'pdf' : 'other'

  if (previewType === 'other') {
    return
  }

  previewModal.value = {
    open: true,
    url: attachment.url,
    fileName: attachment.name,
    type: previewType,
    index: attachment.index
  }

  // Empêcher le scroll du body quand le modal est ouvert
  document.body.style.overflow = 'hidden'
}

const closePreview = () => {
  previewModal.value.open = false
  document.body.style.overflow = ''
}

const previousDocument = () => {
  if (previewModal.value.index > 0) {
    const newIndex = previewModal.value.index - 1
    const attachment = mappedDocuments.value[newIndex]
    if (attachment) {
      openPreviewFromAttachment(attachment)
    }
  }
}

const nextDocument = () => {
  if (previewModal.value.index < mappedDocuments.value.length - 1) {
    const newIndex = previewModal.value.index + 1
    const attachment = mappedDocuments.value[newIndex]
    if (attachment) {
      openPreviewFromAttachment(attachment)
    }
  }
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.style.display = 'none'
}

const getComponentForType = (type: AttachmentType) => {
  if (type === 'image') return ImageAttachmentCard
  if (type === 'pdf') return PdfAttachmentCard
  if (type === 'word' || type === 'excel' || type === 'powerpoint' || type === 'cv') {
    return OfficeAttachmentCard
  }
  return GenericAttachmentCard
}
</script>
